import { RiCalendarLine } from "@itandi/itandi-bb-ui";
import { InputRadio } from "@itandi/itandi-bb-ui/experimental/InputRadio";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputDatev2 } from "@itandi/itandi-bb-ui/InputDatev2";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { PresentationChip } from "@itandi/itandi-bb-ui/PresentationChip";
import { useFormContext } from "react-hook-form";
import { getWitnessLabel } from "~/models/Deal";
import type { DealUpdateParam } from "~/models/DealForm";
import { getNextWitnessType, isWitnessRequired } from "~/models/DealForm";
import { toDate } from "~/utils/DateUtil";
import { onChangeDate } from "~/utils/FormUtil";
import Styles from "./Block.module.scss";

const witnessFieldNames = [
	"witnessName",
	"witnessNameKana",
	"witnessTel",
	"witnessEmail"
] as const;

export function MoveOutBlock() {
	const { register, watch, formState, setValue, clearErrors } =
		useFormContext<DealUpdateParam>();
	const { errors } = formState;
	const dealType = watch("dealType");
	const terminationDate = watch("terminationDate");
	const witnessType = watch("witnessType");
	const inspectionBy = watch("inspectionBy");
	const isRestorationDealType = dealType === "restoration";
	const witnessRequired = isWitnessRequired({
		dealType,
		inspectionBy,
		witnessType
	});
	const showWitnessType = !isRestorationDealType || inspectionBy !== "none";
	const witnessLabel = getWitnessLabel(dealType);

	const onWitnessTypeChange = (value: DealUpdateParam["witnessType"]): void => {
		setValue("witnessType", value, { shouldValidate: true });
		if (value !== "specified") {
			clearErrors(witnessFieldNames);
		}
	};

	const onInspectionByChange = (
		value: DealUpdateParam["inspectionBy"]
	): void => {
		setValue("inspectionBy", value, { shouldValidate: true });
		const nextWitnessType = getNextWitnessType({
			currentWitnessType: witnessType,
			dealType,
			inspectionBy: value
		});
		setValue("witnessType", nextWitnessType, { shouldValidate: true });
		if (nextWitnessType !== "specified") {
			clearErrors(witnessFieldNames);
		}
	};

	return (
		<div>
			<div className={Styles.FormRow}>
				<div className={Styles.FormRowLabel}>
					<RiCalendarLine className={Styles.BlockIcon} />
					{isRestorationDealType ? "退去・立会情報" : "訪問情報"}
				</div>
			</div>
			{isRestorationDealType ? (
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabelCol}>
						<span className={Styles.FormLabelText}>立会担当会社</span>
						<PresentationChip variant="red">必須</PresentationChip>
					</div>
					<div className={Styles.FormSettingCol}>
						<div className={Styles.InputRadioGroup}>
							<InputRadio
								checked={inspectionBy === "vendor"}
								label="施工会社"
								name="inspectionBy"
								onChange={(): void => {
									onInspectionByChange("vendor");
								}}
								value="vendor"
							/>
							<InputRadio
								checked={inspectionBy === "company"}
								label="管理会社"
								name="inspectionBy"
								onChange={(): void => {
									onInspectionByChange("company");
								}}
								value="company"
							/>
							<InputRadio
								checked={inspectionBy === "none"}
								label="なし"
								name="inspectionBy"
								onChange={(): void => {
									onInspectionByChange("none");
								}}
								value="none"
							/>
						</div>
						{errors.inspectionBy ? (
							<div className={Styles.InvalidFeedback}>
								{errors.inspectionBy.message}
							</div>
						) : null}
					</div>
				</div>
			) : null}
			{showWitnessType ? (
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabelCol}>
						<span className={Styles.FormLabelText}>{witnessLabel}</span>
						<PresentationChip variant="red">必須</PresentationChip>
					</div>
					<div className={Styles.FormSettingCol}>
						<div className={Styles.InputRadioGroup}>
							<InputRadio
								checked={witnessType === "tenant"}
								label="契約者"
								name="witnessType"
								onChange={(): void => {
									onWitnessTypeChange("tenant");
								}}
								value="tenant"
							/>
							<InputRadio
								checked={witnessType === "specified"}
								label="契約者以外"
								name="witnessType"
								onChange={(): void => {
									onWitnessTypeChange("specified");
								}}
								value="specified"
							/>
							{!isRestorationDealType ? (
								<InputRadio
									checked={witnessType === "none"}
									label="なし"
									name="witnessType"
									onChange={(): void => {
										onWitnessTypeChange("none");
									}}
									value="none"
								/>
							) : null}
						</div>
						{errors.witnessType ? (
							<div className={Styles.InvalidFeedback}>
								{errors.witnessType.message}
							</div>
						) : null}
					</div>
				</div>
			) : null}
			{witnessType === "specified" ? (
				<>
					<div className={Styles.FormRow}>
						<div className={Styles.FormLabelCol}>
							<span className={Styles.FormLabelText}>{witnessLabel} 氏名</span>
							{witnessRequired ? (
								<PresentationChip variant="red">必須</PresentationChip>
							) : null}
						</div>
						<div className={Styles.FormSettingCol}>
							氏名
							<Input
								isInvalid={!!errors.witnessName}
								placeholder="例）内装 太郎"
								{...register("witnessName")}
							/>
							{errors.witnessName ? (
								<div className={Styles.InvalidFeedback}>
									{errors.witnessName.message}
								</div>
							) : null}
							カナ
							<Input
								isInvalid={!!errors.witnessNameKana}
								placeholder="例）ナイソウ タロウ"
								{...register("witnessNameKana")}
							/>
							{errors.witnessNameKana ? (
								<div className={Styles.InvalidFeedback}>
									{errors.witnessNameKana.message}
								</div>
							) : null}
						</div>
					</div>
					<div className={Styles.FormRow}>
						<div className={Styles.FormLabelCol}>
							<span className={Styles.FormLabelText}>
								{witnessLabel} 電話番号
							</span>
							{witnessRequired ? (
								<PresentationChip variant="red">必須</PresentationChip>
							) : null}
						</div>
						<div className={Styles.FormSettingCol}>
							<Input
								isInvalid={!!errors.witnessTel}
								placeholder="例）090-0000-0000"
								{...register("witnessTel")}
							/>
							{errors.witnessTel ? (
								<div className={Styles.InvalidFeedback}>
									{errors.witnessTel.message}
								</div>
							) : null}
						</div>
					</div>
					<div className={Styles.FormRow}>
						<div className={Styles.FormLabelCol}>
							<span className={Styles.FormLabelText}>
								{witnessLabel} メールアドレス
							</span>
						</div>
						<div className={Styles.FormSettingCol}>
							<Input
								isInvalid={!!errors.witnessEmail}
								placeholder="例）sample@example.com"
								{...register("witnessEmail")}
							/>
							{errors.witnessEmail ? (
								<div className={Styles.InvalidFeedback}>
									{errors.witnessEmail.message}
								</div>
							) : null}
						</div>
					</div>
				</>
			) : null}
			{witnessType !== "none" ? (
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabelCol}>
						<span className={Styles.FormLabelText}>日程調整時の注意事項</span>
					</div>
					<div className={Styles.FormSettingCol}>
						<InputText
							isInvalid={!!errors.inspectionScheduleAdjustmentNote}
							placeholder={
								isRestorationDealType
									? "例）当日の立会人ではなく、契約者の田中様（03-1234-5678）へご連絡ください。"
									: "例）平日18時以降に連絡してください。"
							}
							rows={5}
							{...register("inspectionScheduleAdjustmentNote")}
						/>
						{errors.inspectionScheduleAdjustmentNote ? (
							<div className={Styles.InvalidFeedback}>
								{errors.inspectionScheduleAdjustmentNote.message}
							</div>
						) : null}
						<p className={Styles.Description}>
							{`${witnessLabel}以外へ日程調整の連絡が必要な場合や、連絡可能時間などがあれば入力してください。`}
						</p>
					</div>
				</div>
			) : null}
			{isRestorationDealType ? (
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabelCol}>
						<span className={Styles.FormLabelText}>解約日</span>
					</div>
					<div className={Styles.FormSettingCol}>
						<div>
							<InputDatev2
								maxWidth="13rem"
								onChange={(date): void => {
									onChangeDate(date, "terminationDate", setValue);
								}}
								value={toDate(terminationDate)}
							/>
						</div>
					</div>
				</div>
			) : null}
		</div>
	);
}
